export class SwitchSound {
  private context: AudioContext | null = null;
  private noiseBuffer: AudioBuffer | null = null;

  async play(on: boolean): Promise<void> {
    const context = this.getContext();
    if (context.state === 'suspended') await context.resume();

    const now = context.currentTime;
    const limiter = context.createDynamicsCompressor();
    limiter.threshold.value = -6;
    limiter.knee.value = 3;
    limiter.ratio.value = 12;
    limiter.attack.value = 0.001;
    limiter.release.value = 0.08;
    const output = context.createGain();
    output.gain.value = 1.65;
    limiter.connect(output).connect(context.destination);

    const master = context.createGain();
    master.gain.setValueAtTime(0.0001, now);
    master.gain.exponentialRampToValueAtTime(1.15, now + 0.002);
    master.gain.exponentialRampToValueAtTime(0.0001, now + 0.11);
    master.connect(limiter);

    // A short filtered noise transient supplies the physical "snap".
    const noise = context.createBufferSource();
    noise.buffer = this.getNoiseBuffer(context);
    const filter = context.createBiquadFilter();
    filter.type = 'bandpass';
    filter.frequency.setValueAtTime(on ? 2400 : 1850, now);
    filter.Q.value = 1.1;
    noise.connect(filter).connect(master);
    noise.start(now);
    noise.stop(now + 0.085);

    // A tiny pitched body gives ON and OFF distinguishable mechanical weight.
    const body = context.createOscillator();
    const bodyGain = context.createGain();
    body.type = 'triangle';
    body.frequency.setValueAtTime(on ? 520 : 390, now);
    body.frequency.exponentialRampToValueAtTime(on ? 300 : 250, now + 0.04);
    bodyGain.gain.setValueAtTime(0.72, now);
    bodyGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.095);
    body.connect(bodyGain).connect(master);
    body.start(now);
    body.stop(now + 0.1);
  }

  private getContext(): AudioContext {
    this.context ??= new AudioContext();
    return this.context;
  }

  private getNoiseBuffer(context: AudioContext): AudioBuffer {
    if (this.noiseBuffer) return this.noiseBuffer;
    const length = Math.ceil(context.sampleRate * 0.12);
    const buffer = context.createBuffer(1, length, context.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < length; i += 1) {
      data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / length, 2);
    }
    this.noiseBuffer = buffer;
    return buffer;
  }
}
